/**
 * CountdownMentionNode - Tiptap extension for @countdown mentions
 *
 * Creates inline atom nodes for template countdown timers (e.g., @offerTimer, @saleCountdown)
 * These render as styled chips in the editor and serialize to @timerName format.
 *
 * Follows LinkMentionNode pattern exactly.
 */

import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { CountdownMentionChip } from '../components/CountdownMentionChip';

export interface CountdownMentionNodeAttrs {
  /** Countdown identifier/name (e.g., "offerTimer") */
  id: string;
  /** Optional label tag wrapping this mention (e.g., "highlight") */
  labelTag: string | null;
}

export const CountdownMentionNode = Node.create({
  name: 'countdownMention',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-countdown-id'),
        renderHTML: (attributes) => ({
          'data-countdown-id': attributes.id,
        }),
      },
      labelTag: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-label-tag'),
        renderHTML: (attributes) => {
          if (!attributes.labelTag) return {};
          return { 'data-label-tag': attributes.labelTag };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-countdown-id]',
        getAttrs: (domNode) => {
          const dom = domNode as HTMLElement;
          return {
            id: dom.getAttribute('data-countdown-id'),
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        class: 'countdown-mention',
        'data-type': 'countdownMention',
      }),
      `@${HTMLAttributes['data-countdown-id'] || ''}`,
    ];
  },

  /**
   * Serialize to plain text format: @timerName
   * This is used when extracting text content from the editor
   */
  renderText({ node }) {
    return `@${node.attrs.id || ''}`;
  },

  addNodeView() {
    return ReactNodeViewRenderer(CountdownMentionChip);
  },
});

export default CountdownMentionNode;
